import { useCallback, useRef, useContext, useState, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import cruiseGlossaryMessages from 'hooks/Cruise/cruiseGlossaryMessages'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import IconButton from 'LuxKit/Button/IconButton'
import Group from 'components/utils/Group'
import CruiseLinesSearchList from './CruiseLineSelectContentList'
import CruiseFilterFooter from '../Common/CruiseFilterFooter'
import useToggle from 'hooks/useToggle'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'

interface Props {
  cruiseLineNames: Array<string>
  onChange: (values: App.CruiseGlobalFilters) => void
  drawerMode?: boolean
  handlerSearchTerm: (e: React.ChangeEvent<HTMLInputElement>) => void
  handlerClearSearchTerm: () => void
  searchTerm: string
}

function CruiseLineSelectMobile(props: Props) {
  const {
    cruiseLineNames,
    onChange,
    drawerMode,
    handlerSearchTerm,
    handlerClearSearchTerm,
    searchTerm,
  } = props
  const intl = useIntl()
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)
  const inputSearchRef = useRef<HTMLInputElement>(null)
  const [isOpen, toggleModal] = useToggle(false)
  const [showAll, setShowAll] = useState(false)
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const globalSearchState = useContext(GlobalSearchStateContext)

  const { cruiseLines } = globalSearchState

  const handleOnApply = useCallback(() => {
    trackClientEvent({
      subject: 'filter_applied',
      action: 'applied',
      category: 'cruise_line_filter',
      type: 'interaction',
    })

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'filters' },
      sortContext: { value: 'recommended' },
      searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
      filterContexts: cruiseLines.map(cruiseLine => ({
        label: 'Cruise Line',
        type: 'cruise_line',
        value: cruiseLine,
      })),
    }))

    onChange({ cruiseLines })
    toggleModal()
  }, [onChange, cruiseLines, toggleModal, globalSearchState])

  const onSelect = useCallback((cruiseLines: Array<string>) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_CRUISE_LINES, cruiseLines })
  }, [searchDispatch])

  const onClear = useCallback(() => {
    trackClientEvent({
      subject: 'filter_cleared',
      action: 'cleared',
      category: 'cruise_line_filter',
      type: 'interaction',
    })
    searchDispatch({ type: GlobalSearchStateActions.SET_CRUISE_LINES, cruiseLines: [] })
    setShowAll(false)
    handlerClearSearchTerm()
  }, [handlerClearSearchTerm, searchDispatch])

  const cruiseLineLabel = useMemo(() => {
    if (isOpen) return searchTerm

    const [firstName] = cruiseLineNames
    const more = cruiseLineNames.length > 1 ? `+${cruiseLineNames.length - 1}` : ''
    return firstName && `${firstName} ${more}`
  }, [cruiseLineNames, isOpen, searchTerm])

  const cruiseLinesLabel = intl.formatMessage({
    id: '9FdWIO4Y_3a5V422EHkU',
    defaultMessage: 'Cruise lines',
    description: 'Label for cruise lines filter input in the cruise offer or booking section',
  })

  const searchCruiseLinesPlaceholder = intl.formatMessage({
    id: 'yu-x9cjn8Wt2HeAObpNI',
    defaultMessage: 'Search cruise lines',
    description: 'Placeholder text when searching for cruise lines in the cruises section',
  })

  const allCruiseLinesPlaceholder = intl.formatMessage(
    cruiseGlossaryMessages.allCruiseLines,
  )

  return <>
    <SearchFormField
      data-testid="cruise-line-select-mobile-input"
      onClick={toggleModal}
      ref={dropdownTriggerRef}
      label={cruiseLinesLabel}
      placeholder={isOpen ? searchCruiseLinesPlaceholder : allCruiseLinesPlaceholder}
      value={cruiseLineLabel}
      displayValue={cruiseLineLabel}
    />

    <ModalBase
      mode="drawer"
      isOpen={isOpen}
      onClose={toggleModal}
    >
      <ModalHeader
        title={cruiseLinesLabel}
        onCloseButtonClick={toggleModal}
      />
      <ModalBody>
        <ModalContent flushListItems>
          <Group direction="vertical" gap={16}>
            <TextInputOnly
              ref={inputSearchRef}
              noValidationSpacing
              placeholder={searchCruiseLinesPlaceholder}
              value={searchTerm}
              onChange={handlerSearchTerm}
              endIcon={searchTerm.length > 0 ?
                  <IconButton kind="tertiary" onClick={handlerClearSearchTerm}><LineTimesIcon /></IconButton> :
                  <LineSearchIcon />}
            />
            <CruiseLinesSearchList
              inputSearchRef={inputSearchRef}
              searchTerm={searchTerm}
              showAll={showAll}
              onSetShowAll={setShowAll}
              onSelect={onSelect}
              drawerMode={drawerMode}
            />
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <CruiseFilterFooter
          onApply={handleOnApply}
          onClear={onClear}
        />
      </ModalFooter>
    </ModalBase>
  </>
}

export default CruiseLineSelectMobile
